
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { PlusCircle, Edit, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import BlogEditDialog from '@/components/admin/BlogEditDialog';
import { Tables } from '@/integrations/supabase/types';

type BlogPost = Tables<'blog_posts'>;

const AdminBlog: React.FC = () => {
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['blog_posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
  
  const handleAddNew = () => {
    setSelectedPost(null);
    setEditDialogOpen(true);
  };
  
  const handleEdit = (post: BlogPost) => {
    setSelectedPost(post);
    setEditDialogOpen(true);
  };

  const handleDelete = async (postId: string) => {
    try {
      const { error } = await supabase.from('blog_posts').delete().eq('id', postId);
      if (error) throw error;
      toast.success('Post deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
    } catch (error: any) {
      toast.error('Error deleting post', { description: error.message });
    }
  };

  const handleCreateSamplePost = async () => {
    const samplePost = {
      title: 'My First Blog Post',
      slug: 'my-first-blog-post',
      content: `This is a sample post created for you to edit.
  
You can use markdown to format your content. For example:
  
*   Lists are easy
*   Just use asterisks
  
## Headings
  
You can use hashes for headings.
  
> Blockquotes are also supported.
  
Enjoy editing!`,
      author_name: 'Admin',
      status: 'published' as const,
      meta_title: 'My First Blog Post',
      meta_description: 'A sample blog post created automatically for editing.',
      meta_keywords: 'sample, blog, first post, getting started',
      featured_image_url: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?q=80&w=2070&auto=format&fit=crop',
      published_at: new Date().toISOString(),
    };

    try {
      // Check if a sample post with the same slug already exists
      const { data: existingPost } = await supabase
        .from('blog_posts')
        .select('id')
        .eq('slug', samplePost.slug)
        .maybeSingle();

      if (existingPost) {
        toast.info('A sample post already exists.');
        return;
      }

      const { error } = await supabase.from('blog_posts').insert([samplePost]);
      if (error) throw error;
      toast.success('Sample blog post created successfully!');
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      queryClient.invalidateQueries({ queryKey: ['public_blog_posts'] });
    } catch (error: any) {
      toast.error('Error creating sample post', { description: error.message });
    }
  };

  if (error) {
    toast.error('Failed to load posts', { description: error.message });
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Manage Blog</h1>
        <div className="flex items-center space-x-2">
          <Button onClick={handleAddNew}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add New Post
          </Button>
          <Button onClick={handleCreateSamplePost} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Create Sample Post
          </Button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        {isLoading ? (
          <p>Loading posts...</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts?.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium">{post.title}</TableCell>
                  <TableCell>{post.author_name}</TableCell>
                  <TableCell>
                    <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                      {post.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(post.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(post)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the blog post.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(post.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <BlogEditDialog 
        isOpen={isEditDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        post={selectedPost}
      />
    </div>
  );
};

export default AdminBlog;
